import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '../stores/authStore.js'

const routes = [
  {
    path: '/auth/callback',
    name: 'AuthCallback',
    component: () => import('../pages/AuthCallback.vue'),
    meta: { public: true },
  },
  {
    // Local-dev Okta redirect URI lands here. main.js intercepts the
    // ?code=&state=... callback BEFORE the router mounts and rewrites the
    // URL to the intended target path.
    path: '/leaderboard',
    name: 'PointsLeaderboard',
    component: () => import('../pages/Winners.vue'),
  },
  {
    path: '/reports',
    name: 'Reports',
    component: () => import('../pages/Reports.vue'),
    meta: { requiredPermissions: ['reports:view:all'] },
  },
  // Landing (public so post-logout doesn't auto-re-authenticate)
  { path: '/', name: 'Landing', component: () => import('../pages/InitiativesHubLanding.vue'), meta: { public: true } },

  // Top-level
  { path: '/explore', redirect: '/search' },
  { path: '/submit', name: 'SubmitInitiative', component: () => import('../pages/AddInitiative.vue'), meta: { requiredPermissions: ['initiative:create'] } },
  { path: '/profile', name: 'Profile', component: () => import('../pages/Profile.vue') },
  { path: '/notifications', name: 'Notifications', component: () => import('../pages/Notifications.vue') },
  { path: '/approvals', name: 'ApprovalInbox', component: () => import('../pages/ApprovalInbox.vue') },
  { path: '/search', name: 'Search', component: () => import('../pages/Search.vue') },
  // Public on purpose: the people most likely to need support are the ones
  // who cannot get past the sign-in gate. The page adapts — signed-out
  // visitors get the channels and the FAQ, signed-in users get the form.
  { path: '/contact', name: 'Contact', component: () => import('../pages/Contact.vue'), meta: { public: true } },
  // The dashboard opens on the whole organization; the BU and Sub-BU scopes
  // below are the same page narrowed by route params.
  { path: '/dashboard', name: 'OrgDashboard', component: () => import('../pages/dashboards/BUDashboard.vue') },
  { path: '/dashboard/bu/:bu', name: 'BUDashboard', component: () => import('../pages/dashboards/BUDashboard.vue') },
  { path: '/dashboard/bu/:bu/sub/:subBu', name: 'SubBUDashboard', component: () => import('../pages/dashboards/BUDashboard.vue') },
  { path: '/dashboard/approver', name: 'ApproverDashboard', component: () => import('../pages/dashboards/ApproverDashboard.vue') },
  { path: '/initiatives', name: 'InitiativeDirectory', component: () => import('../pages/InitiativeDirectory.vue') },
  { path: '/initiatives/:id/edit', name: 'InitiativeEdit', component: () => import('../pages/AddInitiative.vue'), meta: { requiredPermissions: ['initiative:edit:own'] } },
  { path: '/initiatives/:id', name: 'InitiativeDetail', component: () => import('../pages/InitiativeDetail.vue') },
  { path: '/hackathon', redirect: '/' },
  { path: '/hackathon/:id', redirect: '/' },
  { path: '/stretch', redirect: '/' },
  { path: '/stretch/new', redirect: '/' },
  { path: '/stretch/:id', redirect: '/' },
  { path: '/stretch/:id/edit', redirect: '/' },

  // Legacy program URLs remain as redirects while the codebase is being retired.
  { path: '/ci/dashboard', redirect: '/dashboard' },
  { path: '/ci/initiatives', redirect: '/initiatives' },
  { path: '/ci/initiatives/:id/edit', redirect: to => `/initiatives/${to.params.id}/edit` },
  { path: '/ci/initiatives/:id', redirect: to => `/initiatives/${to.params.id}` },
  { path: '/ci/awards', redirect: '/leaderboard' },
  { path: '/ci/panelist-ratings', name: 'CIPanelistRatings', component: () => import('../pages/PanelistRating.vue') },

  // Admin / Management routes
  //
  // Admin covers exactly two things: who has access, and how the organization
  // is structured. There is no landing page — an overview that only summarises
  // two modules is a screen users pass through rather than use, so `/admin`
  // opens directly on the roster.
  { path: '/admin', redirect: '/admin/users' },
  { path: '/admin/users', name: 'AdminUsers', component: () => import('../pages/admin/UserManagement.vue'), meta: { requiredRoles: ['admin', 'program_manager'] } },
  { path: '/admin/organization', name: 'AdminOrganization', component: () => import('../pages/admin/OrganizationManagement.vue'), meta: { requiredRoles: ['admin', 'program_manager'] } },
  { path: '/admin/support', name: 'AdminSupport', component: () => import('../pages/admin/SupportInbox.vue'), meta: { requiredRoles: ['admin', 'program_manager'] } },

  // Redirects
  { path: '/ci/submit', redirect: '/submit' },
  { path: '/winners', redirect: '/leaderboard' },
  { path: '/admin/business-units', redirect: '/admin/organization' },
  { path: '/admin/bu-approvers', redirect: '/admin/organization' },
  { path: '/admin/panelists', redirect: '/admin/users' },
  { path: '/admin/evaluators', redirect: '/admin/users' },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  /**
   * Restore position on back/forward, honour in-page anchors (used by the
   * "Notification settings" links), and otherwise reset to the top — without
   * this, navigating between pages silently keeps the previous scroll offset.
   */
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition
    if (to.hash) {
      return {
        el: to.hash,
        top: 80,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      }
    }
    return { top: 0 }
  },
})

/**
 * Global navigation guard — enforces OKTA authentication on ALL routes.
 * Only routes with meta.public: true bypass authentication.
 */
router.beforeEach(async (to) => {
  // Allow public routes (e.g. auth callback) through without auth
  if (to.meta.public) return true

  const authStore = useAuthStore()

  // ─── Wait for initial auth hydration to complete ─────────────────
  // Prevents redirect loop: without this, the guard would fire before
  // loadUser() finishes and incorrectly conclude the user is unauthenticated.
  await authStore.waitUntilReady()

  // ─── Authentication Gate ─────────────────────────────────────────
  // ALL routes require authentication unless explicitly marked public
  if (!authStore.isAuthenticated) {
    await authStore.signin(to.fullPath)
    return false // navigation will resume after Okta redirect
  }

  // ─── Role gating ────────────────────────────────────────────────
  const requiredRoles = to.meta.requiredRoles
  if (requiredRoles && requiredRoles.length) {
    const ok = requiredRoles.some((r) => authStore.hasRole(r))
    if (!ok) return { path: '/' }
  }

  // ─── Permission gating ──────────────────────────────────────────
  const requiredPermissions = to.meta.requiredPermissions
  if (requiredPermissions && requiredPermissions.length) {
    const ok = requiredPermissions.every((p) => authStore.hasPermission(p))
    if (!ok) return { path: '/' }
  }

  return true
})

export default router
